import React, { useState, useEffect, useMemo } from "react";
import {
    Stack,
    Toggle,
    Dropdown,
    TextField,
    IconButton,
    TooltipHost,
    PrimaryButton,
    DefaultButton,
    DirectionalHint,
    Callout
} from "@fluentui/react";
import { TbSchema } from "react-icons/tb";
import { FaFileImport, FaFileExport } from "react-icons/fa";
import {
    convoLangOptionsCCA,
    convoPurposeOptionsCCA,
    convoRateOptionsCCA,
    convoLookbackMinutesCCA,
    convoTTSOptionsCCA,
    scenExpandPrepend
} from "../../lsts";
import { assistantCardItems, multiPurposeAssistantCreationData } from "../../lsts";
import { serializeLangKeys, parseLangKeys } from "../../util_glob";
import styles from "./CallAssistantSettings.module.css";
import { CCAFieldSelection } from "../../components/DocFieldSelection/CCAFieldSelection";
import {
    stackStyles,
    tooltipStyles,
    dropdownStyles,
    textFieldStyles,
    toggleStylesCompEdit,
    textFieldStylesScenarios,
    invFieldConfMainSaveButStyles,
    textFieldStylesSingleScenarios,
    textFieldStylesScenarioMultilineV2,
    textFieldTitleStylesSingleScenarios,
    invFieldScenarioAddButStyles,
    addScenPrimaryButton,
    impWordsTagInputStyles,
    dropdownCampaignStyles,
    toggleStylesCompDynReEdit
} from "./../../styles_glob";
import { gselectedgroup, gloggedinuser, gfileassourceoption, gconvoconfig, gextrprop, gcampaign, gcampaignkv } from "../../interfaces";
import { getHeaderG, qGetCampaigns } from "../../api/qpi";
import {upsertGroup} from "../../api";

import { HighlightedMultiline } from "./HighlightedMultiline";
import {DateRangePicker} from '../DateRangePicker/DateRangePicker';
import {parseDate} from '@internationalized/date';
import {qAddCampaign} from '../../api/qpi';
import {
    get_local_cache_selected_campaign_id_oer_space,
    set_local_cache_selected_campaign_id_oer_space,
    getActiveCampaignId,
    get_campaigns_for_selected_group
} from "../../pages/chat/ChatUtil";
// ── Per-campaign key-value helpers ──
const getCampaignValue = (arr: gcampaignkv[] | undefined, key: string): string => {
    return arr?.find(e => e.campaign === key)?.value || '';
};
const setCampaignValue = (arr: gcampaignkv[] | undefined, key: string, value: string): gcampaignkv[] => {
    const entries = [...(arr || [])];
    const idx = entries.findIndex(e => e.campaign === key);
    if (idx >= 0) entries[idx] = { campaign: key, value };
    else entries.push({ campaign: key, value });
    return entries;
};
import { toast } from "react-toastify";
import { TbArrowAutofitDown, TbArrowAutofitUp } from "react-icons/tb";
import { TagPicker, ITag, IBasePickerSuggestionsProps, ValidationState } from "@fluentui/react";
import { toIsoDateStr, getDateRange } from "../../util_date_glob";

// ── Call Window helpers ──
const CALL_HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
    key:  `${i.toString().padStart(2, '0')}:00`,
    text: `${i.toString().padStart(2, '0')}:00`,
}));

const CallWindowBar: React.FC<{ start?: string; end?: string }> = ({ start = '08:00', end = '18:00' }) => {
    const startH = Math.min(23, Math.max(0, parseInt((start || '08:00').split(':')[0], 10)));
    const endH   = Math.min(24, Math.max(0, parseInt((end   || '18:00').split(':')[0], 10)));
    return (
        <div className={styles.cwTimeline}>
            {Array.from({ length: 24 }, (_, i) => (
                <div
                    key={i}
                    className={`${styles.cwHour} ${i >= startH && i < endH ? styles.cwHourActive : ''}`}
                    title={`${i.toString().padStart(2, '0')}:00`}
                >
                    {i % 6 === 0 && <span className={styles.cwHourLabel}>{i}</span>}
                </div>
            ))}
        </div>
    );
};


interface PropertiesCallProps {
    selectedGroup: gselectedgroup;
    fileList: gfileassourceoption[];
    loggedInUser: gloggedinuser;
    purpose: string;
    onEditDocExtractionDone: () => void;
}

export const CallAssistantSettings: React.FC<PropertiesCallProps> = ({ selectedGroup, fileList, loggedInUser, purpose, onEditDocExtractionDone }) => {
    const FIELD_SELECTION_SECTION_ID = "fieldSelection";
    const GENERAL_INSTRUCTIONS_SECTION_ID = "generalScenarioInstructions";
    const [editedSpace, setEditedSpace] = useState<gselectedgroup>({ ...selectedGroup });
    const [isLoadingAPI, setIsLoadingAPI] = useState(false);
    // keep docextractionconfig as state
    const [docextractionconfig, setDocExtractionConfig] = useState<gextrprop[]>(() => {
        try {
            return selectedGroup.docextractionconfig || [];
        } catch (e) {
            console.error("Error parsing docextractionconfig", e);
            return [];
        }
    });
    const [collapsedScenarios, setCollapsedScenarios] = useState<{ [id: string]: boolean }>({});
    const [isGeneralInstructionsCollapsed, setIsGeneralInstructionsCollapsed] = useState(false);
    const [isFieldSelectionCollapsed, setIsFieldSelectionCollapsed] = useState(false);
    const [draggedScenarioIndex, setDraggedScenarioIndex] = useState<number | null>(null);
    const [isImportCalloutVisible, setIsImportCalloutVisible] = useState(false);
    const [importCalloutTarget, setImportCalloutTarget] = useState<HTMLElement | null>(null);
    const [importScenariosJson, setImportScenariosJson] = useState("");
    const [campaigns, setCampaigns] = useState<gcampaign[]>([]);
    const [activeCampaignId, setActiveCampaignId] = useState<string>(() => getActiveCampaignId(selectedGroup, get_campaigns_for_selected_group(selectedGroup)));
    const scenExpandedStateStorageKey = useMemo(() => `scenExpandedState${selectedGroup.selectionId || ""}`, [selectedGroup.selectionId]);

    type CollapsibleUiState = {
        expandedFields: string[];
        collapsedScenarios: { [id: string]: boolean };
    };

    const getCollapsibleUiStateFromStorage = (storageKey: string): CollapsibleUiState => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (!stored) return { expandedFields: [], collapsedScenarios: {} };
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) {
                return {
                    expandedFields: parsed.filter(v => typeof v === "string"),
                    collapsedScenarios: {}
                };
            }
            const expandedFields = Array.isArray(parsed?.expandedFields)
                ? parsed.expandedFields.filter((v: unknown) => typeof v === "string")
                : [];
            const collapsedScenariosRaw = parsed?.collapsedScenarios;
            const collapsedScenarios: { [id: string]: boolean } = {};
            if (collapsedScenariosRaw && typeof collapsedScenariosRaw === "object") {
                Object.entries(collapsedScenariosRaw).forEach(([k, v]) => {
                    if (typeof v === "boolean") collapsedScenarios[k] = v;
                });
            }
            return { expandedFields, collapsedScenarios };
        } catch {
            return { expandedFields: [], collapsedScenarios: {} };
        }
    };

    const setCollapsibleUiStateToStorage = (storageKey: string, state: CollapsibleUiState) => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(state));
        } catch {
            // ignore storage errors
        }
    };

    useEffect(() => {
        const storedUiState = getCollapsibleUiStateFromStorage(scenExpandedStateStorageKey);
        const expandedFields = storedUiState.expandedFields;
        setIsFieldSelectionCollapsed(!expandedFields.includes(FIELD_SELECTION_SECTION_ID));
        setIsGeneralInstructionsCollapsed(!expandedFields.includes(GENERAL_INSTRUCTIONS_SECTION_ID));

        const scenarios = selectedGroup.convo_config?.scenarios || [];
        const nextCollapsedScenarios: { [id: string]: boolean } = {};
        scenarios.forEach(s => {
            const fromStorage = storedUiState.collapsedScenarios[s.id];
            nextCollapsedScenarios[s.id] = typeof fromStorage === "boolean" ? fromStorage : true;
        });
        setCollapsedScenarios(nextCollapsedScenarios);
    }, [scenExpandedStateStorageKey, selectedGroup.convo_config?.scenarios]);

    useEffect(() => {
        const expandedFields: string[] = [];
        if (!isFieldSelectionCollapsed) expandedFields.push(FIELD_SELECTION_SECTION_ID);
        if (!isGeneralInstructionsCollapsed) expandedFields.push(GENERAL_INSTRUCTIONS_SECTION_ID);

        setCollapsibleUiStateToStorage(scenExpandedStateStorageKey, {
            expandedFields,
            collapsedScenarios
        });
    }, [isFieldSelectionCollapsed, isGeneralInstructionsCollapsed, collapsedScenarios, scenExpandedStateStorageKey]);

    //useEffect(() => {
    //    console.log("‹editedSpace›", editedSpace);
    //}, [editedSpace]);


    useEffect(() => {
        setEditedSpace({ ...selectedGroup });
        try {
            setDocExtractionConfig(selectedGroup.docextractionconfig || []);
        } catch (e) {
            setDocExtractionConfig([]);
        }
    }, [selectedGroup]);

    // fetch campaigns for this space when purpose is call
    useEffect(() => {
        if (purpose === "call" && selectedGroup.selectionId) {
            qGetCampaigns(loggedInUser, selectedGroup.selectionId)
                .then((data: gcampaign[]) => {
                    setCampaigns(data || []);
                    // reset active campaign if it no longer exists
                    setActiveCampaignId(prev => {
                        if (prev && data?.some(c => c.campaign_id === prev)) return prev;
                        return  getActiveCampaignId(selectedGroup, get_campaigns_for_selected_group(selectedGroup));
                    });
                })
                .catch(() => setCampaigns([]));
        }
    }, [purpose, selectedGroup.selectionId]);


    const campFilter = (docext: any[] | undefined) => {
        try{
            return docext?.filter(f => !f.campaignid || f.campaignid === "root");
        } catch(e){
            return docext;
        }
    }



    // filtered fields and scenarios based on active campaign. if no campaign = show only global (untagged) items; campaign selected = show only that campaign's items
    const filteredDocExtractionConfig = useMemo(() => {
        if (!activeCampaignId || activeCampaignId === "root") return campFilter(docextractionconfig); // root - show all with campaignid which is not 'root'. root ⇒ items can miss campaignid or have root campaignid. For root we show all all that have campaign_id and it is not root
        return docextractionconfig.filter(f => f.campaignid === activeCampaignId); // specific campaign - show only items tagged with that campaignid
    }, [docextractionconfig, activeCampaignId]);

    const filteredScenarios = useMemo(() => {
        const scenarios = editedSpace.convo_config?.scenarios || [];
        if (!activeCampaignId) return scenarios.filter(s => s?.campaignid == null || s.campaignid === "root");

        // root - show all with campaignid which is not 'root'. root ⇒ items can miss campaignid or have root campaignid. For root we show all all that have campaign_id and it is not root
        if (activeCampaignId == "root") return scenarios.filter(s => !s.campaignid || s.campaignid === "root");

        // specific campaign - show only items tagged with that campaignid
        return scenarios.filter(s => s.campaignid === activeCampaignId);
    }, [editedSpace.convo_config?.scenarios, activeCampaignId]);

    // ── campaign-aware helpers: resolved values for active campaign ──
    const isCampaignMode = editedSpace.campaigns_enabled === 1 && campaigns.length > 0;
    const campaignKey = activeCampaignId || 'root';

    // ensure the active campaign has an entry in each array field when first selected. When root entry is empty/missing, seed from the old scalar fields to preserve pre-campaign edits
    useEffect(() => {
        if (!isCampaignMode) return;
        const cc = editedSpace.convo_config;
        const key = activeCampaignId || 'root';
        const patches: Partial<gconvoconfig> = {};

        // greeting
        const hasGreetingEntry = cc?.greetings?.find(e => e.campaign === key);
        if (!hasGreetingEntry || (key === 'root' && !hasGreetingEntry.value && cc?.greeting)) {
            let arr = cc?.greetings || [];
            const rootEntry = arr.find(e => e.campaign === 'root'); // always seed root from scalar if root is empty/missing
            if (!rootEntry || !rootEntry.value) arr = setCampaignValue(arr, 'root', cc?.greeting || '');
            if (key !== 'root') arr = setCampaignValue(arr, key, '');
            patches.greetings = arr;
        }

        // custaiinsts
        const hasCustEntry = cc?.custaiinsts?.find(e => e.campaign === key);
        if (!hasCustEntry || (key === 'root' && !hasCustEntry.value && editedSpace.custaiinst)) {
            let arr = cc?.custaiinsts || [];
            const rootEntry = arr.find(e => e.campaign === 'root');
            if (!rootEntry || !rootEntry.value) arr = setCampaignValue(arr, 'root', editedSpace.custaiinst || '');
            if (key !== 'root') arr = setCampaignValue(arr, key, '');
            patches.custaiinsts = arr;
        }

        // imp_words_arr
        const hasImpEntry = cc?.imp_words_arr?.find(e => e.campaign === key);
        if (!hasImpEntry || (key === 'root' && !hasImpEntry.value && cc?.imp_words)) {
            let arr = cc?.imp_words_arr || [];
            const rootEntry = arr.find(e => e.campaign === 'root');
            if (!rootEntry || !rootEntry.value) arr = setCampaignValue(arr, 'root', cc?.imp_words || '');
            if (key !== 'root') arr = setCampaignValue(arr, key, '');
            patches.imp_words_arr = arr;
        }

        if (Object.keys(patches).length) handleConvoConfigChange("greetings" as any, patches);
    }, [activeCampaignId, isCampaignMode]);

    // resolved active values
    const activeGreeting = useMemo(() => {
        if (!isCampaignMode) return editedSpace.convo_config?.greeting || '';
        return getCampaignValue(editedSpace.convo_config?.greetings, campaignKey);
    }, [editedSpace.convo_config?.greeting, editedSpace.convo_config?.greetings, campaignKey, isCampaignMode]);

    const activeCustAiInst = useMemo(() => {
        if (!isCampaignMode) return editedSpace.custaiinst || '';
        return getCampaignValue(editedSpace.convo_config?.custaiinsts, campaignKey);
    }, [editedSpace.custaiinst, editedSpace.convo_config?.custaiinsts, campaignKey, isCampaignMode]);

    const activeImpWords = useMemo(() => {
        if (!isCampaignMode) return editedSpace.convo_config?.imp_words || '';
        return getCampaignValue(editedSpace.convo_config?.imp_words_arr, campaignKey);
    }, [editedSpace.convo_config?.imp_words, editedSpace.convo_config?.imp_words_arr, campaignKey, isCampaignMode]);

    // update handlers that keep the scalar field synced with root
    const handleGreetingChange = (newValue: string) => {
        if (!isCampaignMode) {
            handleConvoConfigChange("greeting", { greeting: newValue });
            return;
        }
        const updated = setCampaignValue(editedSpace.convo_config?.greetings, campaignKey, newValue);
        const rootVal = campaignKey === 'root' ? newValue : getCampaignValue(updated, 'root');
        handleConvoConfigChange("greetings" as any, { greetings: updated, greeting: rootVal });
    };

    const handleCustAiInstChange = (newValue: string) => {
        if (!isCampaignMode) {
            handleChange("custaiinst", newValue);
            return;
        }
        const updated = setCampaignValue(editedSpace.convo_config?.custaiinsts, campaignKey, newValue);
        const rootVal = campaignKey === 'root' ? newValue : getCampaignValue(updated, 'root');
        handleConvoConfigChange("custaiinsts" as any, { custaiinsts: updated });
        handleChange("custaiinst", rootVal);
    };

    const handleImpWordsChange = (newValue: string) => {
        if (!isCampaignMode) {
            handleConvoConfigChange("imp_words", { imp_words: newValue });
            return;
        }
        const updated = setCampaignValue(editedSpace.convo_config?.imp_words_arr, campaignKey, newValue);
        const rootVal = campaignKey === 'root' ? newValue : getCampaignValue(updated, 'root');
        handleConvoConfigChange("imp_words_arr" as any, { imp_words_arr: updated, imp_words: rootVal });
    };

    const selectedLangKeys = React.useMemo(() => parseLangKeys(editedSpace.convolang), [editedSpace.convolang]);

    // helper to get root field names
    const getRootFieldNames = (fields: gextrprop[]): string[] => {
        return Array.isArray(fields) ? fields.map(f => f.name) : [];
    };

    // helper to extract linked fields from description
    const extractLinkedFields = (description: string, fieldNames: string[]): string[] => {
        if (!description) return [];
        return fieldNames.filter(name => description.includes(name));
    };

    const handleChange = (field: keyof gselectedgroup, value: string | number | boolean | any[]) => {
        if (field === "docextractionconfig" && Array.isArray(value)) setDocExtractionConfig(value as gextrprop[]);
        setEditedSpace(prev => ({ ...prev, [field]: value }));
    };

    const handleConvoConfigChange = (field: keyof gconvoconfig, value: Partial<gconvoconfig>) => {
        setEditedSpace(prev => {
            let newConvoConfig = { ...prev.convo_config, ...value };
            // if scenarios are updated, update linked_fields
            if (field === "scenarios" && Array.isArray(value.scenarios)) {
                const fieldNames = getRootFieldNames(docextractionconfig);
                newConvoConfig.scenarios = value.scenarios.map(scenario => {
                    const linked = extractLinkedFields(scenario.description, fieldNames);
                    return { ...scenario, linked_fields: linked };
                });
            }
            return { ...prev, convo_config: newConvoConfig };
        });
    };

    const cleanProperties = (properties: gextrprop[]): gextrprop[] => {
        if (!Array.isArray(properties)) return [];

        return properties
            .filter(prop => prop.name !== "")
            .map(prop => {
                let cleaned: gextrprop = {
                    name: prop.name.replace(/\s+/g, "_"), // REPLACE SPACES WITH UNDERSCORES
                    description: prop.description,
                    type: prop.type
                };
                if (prop.type == "string" || prop.type == "number") cleaned.validation_source = prop.validation_source;
                if (prop.type == "string" || prop.type == "number") cleaned.identificator = prop.identificator;
                if (prop.type == "string" || prop.type == "number") cleaned.global_prop = prop.global_prop;
                if (prop.type == "string" || prop.type == "number") cleaned.display_name = prop?.display_name?.replace(/[^a-zA-Zа-яА-Я0-9\s]/g, "");
                if (prop.campaignid) cleaned.campaignid = prop.campaignid;

                // IF IT'S AN ARRAY TYPE AND HAS ITEMS, CLEAN THEM RECURSIVELY
                if (prop.type === "array" && prop.items && prop.items.length > 0) {
                    cleaned.items = cleanProperties(prop.items);
                } else if (prop.type === "object" && prop.items && prop.items.length > 0) {
                    cleaned.items = cleanProperties(prop.items);
                }
                return cleaned;
            })
            .filter(prop => {
                // remove props with empty items array when type is 'array'
                if (prop.type === "array" || prop.type === "object") {
                    return prop.items && prop.items.length > 0;
                }
                return true;
            });
    };

    const handleDragStart = (index: number) => {
        setDraggedScenarioIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        if (draggedScenarioIndex === null || draggedScenarioIndex === index) return;

        const scenarios = [...(editedSpace.convo_config.scenarios || [])];
        const draggedItem = scenarios[draggedScenarioIndex];
        scenarios.splice(draggedScenarioIndex, 1);
        scenarios.splice(index, 0, draggedItem);

        handleConvoConfigChange("scenarios", { scenarios });
        setDraggedScenarioIndex(index);
    };

    const handleDragEnd = () => {
        setDraggedScenarioIndex(null);
    };

    const handleSave = () => {
        setIsLoadingAPI(true);
        let cleanedProps = cleanProperties(editedSpace.docextractionconfig);
        setEditedSpace(prev => ({ ...prev, cleanedProps }));
        fetch("/qDocExtrConf", {
            method: "POST",
            headers: getHeaderG(loggedInUser),
            body: JSON.stringify({
                groupid: editedSpace.selectionId,
                docextractionconfig: JSON.stringify(cleanedProps),
                docextractionuseauto: editedSpace.docextractionuseauto,
                custaiinst: editedSpace.custaiinst,
                convo_config: editedSpace.convo_config,
                convolang: editedSpace.convolang
            })
        }).then(() => {
            setIsLoadingAPI(false);
            onEditDocExtractionDone();
        });
    };


    return (
        <div className={styles.editModalWrap}>

            <Stack styles={stackStyles}>
                <div className={styles.editFormSectionTitle}><span>{purpose == "call" ? "Information Collection Settings" : "Content Extraction Settings"}</span></div>

                <div className={styles.editFormSectionTitle}><span>{"General Settings"}</span></div>
                <Stack horizontal horizontalAlign={"stretch"} wrap tokens={{ childrenGap: 20 }} className={styles.docExtrConvoGenSet}>

                    <Stack.Item grow>
                        <TooltipHost content="Language/s that the assistant will expect and listen/respond in." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Dropdown
                                label="Language"
                                className={styles.ccaLangDropdown}
                                options={convoLangOptionsCCA}
                                disabled={editedSpace.convomode == 1 ? false : true}
                                selectedKeys={selectedLangKeys}
                                multiSelect
                                onChange={(_, option) => {
                                    const next = new Set(selectedLangKeys);
                                    if (!option) return;

                                    const k = String(option.key);
                                    if (option.selected) {
                                        next.add(k);
                                        if (option.key != "auto") next.delete("auto")
                                    } else {
                                        next.delete(k);
                                    }
                                    if (next.size === 0) next.add("auto");
                                    handleChange("convolang", serializeLangKeys(Array.from(next)));
                                }}
                                styles={dropdownStyles}
                            />
                        </TooltipHost>
                    </Stack.Item>

                    <Stack.Item grow>
                        <TooltipHost content="How long back in time should the assistant check for previous calls from the same number? This setting allows the assistant to keep context about ended calls and continue from where they left off." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Dropdown
                                label="History Lookback"
                                className={styles.ccaLangDropdown}
                                options={convoLookbackMinutesCCA}
                                disabled={false}
                                selectedKey={editedSpace.convo_config?.lookback_mins || 0}
                                onChange={(_, newValue) => handleConvoConfigChange("lookback_mins", { lookback_mins: newValue ? parseInt(newValue.key.toString()) : 0 })}
                                styles={dropdownStyles}
                            />
                        </TooltipHost>
                    </Stack.Item>

                    <Stack.Item grow>
                        <TooltipHost content="The Voice of the Assistant" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Dropdown
                                label="Voice"
                                className={styles.ccaLangDropdown}
                                options={convoTTSOptionsCCA}
                                disabled={editedSpace.convomode == 1 ? false : true}
                                selectedKey={editedSpace.convo_config.tts || 0}
                                onChange={(_, newValue) => handleConvoConfigChange("tts", { tts: newValue ? parseInt(newValue.key.toString()) : 0 })}
                                styles={dropdownStyles}
                            />
                        </TooltipHost>
                    </Stack.Item>

                    <Stack.Item grow>
                        <TooltipHost content="How quick is the assistant's speech relative to baseline?" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Dropdown
                                label="Speech Rate"
                                className={styles.ccaLangDropdown}
                                options={convoRateOptionsCCA}
                                disabled={editedSpace.convomode == 1 ? false : true}
                                selectedKey={editedSpace.convo_config.rate || 0}
                                onChange={(_, newValue) => handleConvoConfigChange("rate", { rate: newValue ? parseInt(newValue.key.toString()) : 0 })}
                                styles={dropdownStyles}
                            />
                        </TooltipHost>
                    </Stack.Item>
                </Stack>
                <Stack horizontal horizontalAlign={"stretch"} wrap tokens={{ childrenGap: 20 }} className={styles.docExtrConvoGenSet}>
                <Stack.Item grow>
                        <TooltipHost content="What is the assistant going to do" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Dropdown
                                label="Usecase"
                                className={styles.ccaLangDropdown}
                                options={convoPurposeOptionsCCA}
                                disabled={editedSpace.convomode == 1 ? false : true}
                                selectedKey={editedSpace.convo_config.usecase || 0}
                                onChange={(_, newValue) => handleConvoConfigChange("usecase", { usecase: newValue ? parseInt(newValue.key.toString()) : 0 })}
                                styles={dropdownStyles}
                            />
                        </TooltipHost>
                    </Stack.Item>
                    <Stack.Item grow>
                        <TooltipHost content="Enable latest improved version of Dynamic Structured Data  Retrieval Retrieval v3" directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                            <Toggle
                                label="Dynamic Data v3"
                                styles={toggleStylesCompDynReEdit}
                                onText="On"
                                offText="Off"
                                checked={editedSpace.convo_config?.dyn_struct_latest == 1}
                                onChange={(_event, checked) => {
                                    const val = checked ? 1 : 0;
                                    handleConvoConfigChange("dyn_struct_latest" as any, { dyn_struct_latest: val });
                                }}
                            />
                        </TooltipHost>
                    </Stack.Item>
                </Stack>
                {purpose === "call" && (
                    <>
                        <div className={styles.editFormSectionTitle}><span>{"Campaigns General Settings"}</span></div>
                        <Toggle
                            label="Enable Campaigns"
                            inlineLabel
                            styles={toggleStylesCompEdit}
                            onText="On"
                            offText="Off"
                            checked={editedSpace.campaigns_enabled == 1}
                            onChange={(_event, checked) => {
                                const val = checked ? 1 : 0;
                                handleChange("campaigns_enabled", val);
                                if (!checked) {
                                    setActiveCampaignId('') }
                                else {
                                    setActiveCampaignId('root')
                                }

                                upsertGroup(
                                    getHeaderG(loggedInUser),
                                    {
                                        ...editedSpace,
                                        campaigns_enabled: val,
                                    }
                                ).then(() => onEditDocExtractionDone());

                            }}
                        />
                    </>
                )}
                {purpose === "call" && editedSpace.campaigns_enabled == 1 && campaigns.length > 0 && (
                    <>
                        <div style={{ marginBottom: 8, display: "flex", gap: 16, flexDirection: "row" }}>
                            <Dropdown
                                label="Campaign"
                                placeholder="All"
                                options={[
                                    { key: "", text: "All" },
                                    ...campaigns.map(c => ({ key: c.campaign_id, text: c.campaign_display_name || c.campaign_id }))
                                ]}
                                selectedKey={activeCampaignId}
                                onChange={(_, option) => setActiveCampaignId(option ? String(option.key) : "")}
                                styles={dropdownCampaignStyles}
                            />

                            {/* campaign date range picker — visible when a specific campaign is selected */}
                            {activeCampaignId && (() => {
                                const camp = campaigns.find(c => c.campaign_id === activeCampaignId);

                                const rangeVal = getDateRange(camp);
                                let startStr   = toIsoDateStr(camp?.campaign_start_date);
                                let endStr     = toIsoDateStr(camp?.campaign_end_date);

                                return (
                                    <div className={styles.campaignDatesSection}>
                                        <DateRangePicker
                                            label="Campaign Active Dates"
                                            granularity="day"
                                            value={rangeVal}
                                            onChange={(range) => {
                                                if (!camp) return;

                                                // save immediately on any picker change; if one boundary is missing, keep the currently persisted boundary.
                                                const s = range?.start?.toString() || startStr || '';
                                                const e = range?.end?.toString() || endStr || '';

                                                if (!s && !e) return;

                                                setCampaigns(prev => prev.map(c =>
                                                    c.campaign_id === activeCampaignId
                                                        ? { ...c, campaign_start_date: s, campaign_end_date: e }
                                                        : c
                                                ));
                                                qAddCampaign(
                                                    loggedInUser,
                                                    editedSpace.selectionId,
                                                    camp.campaign_config,
                                                    activeCampaignId,
                                                    s, e,
                                                    camp.campaign_display_name
                                                ).catch(() => toast.error('Failed to save campaign dates'));
                                            }}
                                        />
                                    </div>
                                );
                            })()}
                        </div>

                        <div>
                            <div className={styles.editFormSectionTitle}>
                                <span>Outbound Call Window</span>
                            </div>
                            <div className={styles.cwDesc}>
                                Set the hours during which the assistant is allowed to place outbound calls.
                            </div>
                            <div className={styles.cwRow}>
                                <Dropdown
                                    label="From"
                                    options={CALL_HOUR_OPTIONS}
                                    selectedKey={editedSpace.convo_config?.call_window_start || '08:00'}
                                    onChange={(_, opt) => handleConvoConfigChange("call_window_start" as any, { call_window_start: String(opt?.key || '08:00') })}
                                    styles={{ ...dropdownStyles, root: { minWidth: 90 } }}
                                />
                                <div className={styles.cwTimelineWrap}>
                                    <CallWindowBar
                                        start={editedSpace.convo_config?.call_window_start}
                                        end={editedSpace.convo_config?.call_window_end}
                                    />
                                </div>
                                <Dropdown
                                    label="Until"
                                    options={CALL_HOUR_OPTIONS}
                                    selectedKey={editedSpace.convo_config?.call_window_end || '18:00'}
                                    onChange={(_, opt) => handleConvoConfigChange("call_window_end" as any, { call_window_end: String(opt?.key || '18:00') })}
                                    styles={{ ...dropdownStyles, root: { minWidth: 90 } }}
                                />
                            </div>
                        </div>

                    </>
                )}


                {purpose != "call" && (
                    <Stack horizontal tokens={{ childrenGap: 10 }} className={styles.docExtrTopsettings}>
                        <Toggle
                            label="Automatic Field Determination"
                            inlineLabel
                            styles={toggleStylesCompEdit}
                            onText="On"
                            offText="Off"
                            checked={editedSpace.docextractionuseauto == 1}
                            onChange={(_event, checked) => {
                                handleChange("docextractionuseauto", checked ? 1 : 0);
                            }}
                        />
                    </Stack>
                )}


                <div className={styles.editFormSectionTitle}>
                    <span>{"Situational Settings"}</span>
                </div>
                {purpose == "call" && (
                    <TooltipHost content="How chould the assistant start the conversation with the user." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStyles}>
                        <TextField
                            className={styles.spaceTextFields}
                            styles={textFieldStyles}
                            label="Greeting"
                            placeholder="How chould the assistant start the conversation with the user. Leave empty for user initiated conversations."
                            multiline
                            resizable={false}
                            autoAdjustHeight={true}
                            value={activeGreeting}
                            onChange={(_, newValue) => handleGreetingChange(newValue || "")}
                        />
                    </TooltipHost>
                )}
                {purpose == "call" && (
                    <div className={styles.scenarioItem}>
                        <div className={styles.scenarioItemCollapsable}>
                            <IconButton
                                iconProps={{ iconName: isGeneralInstructionsCollapsed ? "ChevronRight" : "ChevronDown" }}
                                onClick={() => setIsGeneralInstructionsCollapsed(!isGeneralInstructionsCollapsed)}
                                styles={{ root: { marginRight: 8, marginTop: 3, padding: 7, borderRadius: 3 } }}
                                title={isGeneralInstructionsCollapsed ? "Expand" : "Collapse"}
                            />
                            <div className={styles.scenarioItemHeader}>
                                <div className={styles.scenarioNameCollapsed}>General Scenario Instructions</div>
                            </div>
                        </div>

                        <div className={`${styles.collapsibleContent} ${isGeneralInstructionsCollapsed ? styles.collapsed : ''}`}>
                            <div className={styles.scenarioDesc}>
                                <HighlightedMultiline
                                    value={
                                        activeCustAiInst ||
                                        (editedSpace.assistanttype == 'anya' || editedSpace.assistanttype == ''
                                            ? multiPurposeAssistantCreationData.custaiinst
                                            : assistantCardItems.filter(item => item.creationData.assistanttype === editedSpace.assistanttype)[0]?.creationData.custaiinst)
                                    }
                                    highlights={getRootFieldNames(docextractionconfig)}
                                    onChange={(newValue) => {handleCustAiInstChange(newValue || '')}}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.editFormSectionTitle}>
                    <span>{"Data Collection Fields"}</span>
                </div>
                {editedSpace.docextractionuseauto === 0 && (
                    <div className={styles.scenarioItem}>
                        <div className={styles.scenarioItemCollapsable}>
                            <IconButton
                                iconProps={{ iconName: isFieldSelectionCollapsed ? "ChevronRight" : "ChevronDown" }}
                                onClick={() => setIsFieldSelectionCollapsed(!isFieldSelectionCollapsed)}
                                styles={{ root: { marginRight: 8, marginTop: 3, padding: 7, borderRadius: 3 } }}
                                title={isFieldSelectionCollapsed ? "Expand" : "Collapse"}
                            />
                            <div className={styles.scenarioItemHeader}>
                                <div className={styles.scenarioNameCollapsed}>Data to Collect</div>
                            </div>
                        </div>

                        <div className={`${styles.collapsibleContent} ${isFieldSelectionCollapsed ? styles.collapsed : ''}`}>
                            <CCAFieldSelection
                                initialProperties={filteredDocExtractionConfig}
                                onChange={updated => {
                                    if (activeCampaignId && activeCampaignId != "root") {
                                        // force the current campaign id for all visible fields (including imported ones)
                                        const tagged = updated.map(f => ({ ...f, campaignid: activeCampaignId }));
                                        // keep all fields NOT belonging to this campaign, then append updated
                                        const otherFields = docextractionconfig.filter(f => f.campaignid !== activeCampaignId);
                                        const merged = [...otherFields, ...tagged];
                                        setDocExtractionConfig(merged);
                                        handleChange("docextractionconfig", merged);
                                    } else {
                                        // global mode: keep all campaign-tagged fields, replace global ones
                                        const campaignFields = Array.isArray(docextractionconfig) ? docextractionconfig.filter(f => f.campaignid) : [];
                                        const merged = [...updated, ...campaignFields];
                                        setDocExtractionConfig(merged);
                                        handleChange("docextractionconfig", merged);
                                    }
                                }}
                                isRoot={true}
                                fileList={fileList}
                                assistantType={selectedGroup.assistanttype}
                                groupId={selectedGroup.selectionId}
                            />
                        </div>
                    </div>
                )}
                {purpose == "call" && (
                    <div>
                        <div className={styles.editFormSectionTitle}>
                            <span>{"Scenario Definitions"}</span>
                        </div>
                        <div className={styles.docExtrSaveButs}>
                            <DefaultButton
                                styles={addScenPrimaryButton}
                                text="Add Scenario"
                                className={styles.invScenarioAddButStyles}
                                onClick={() => {
                                    const newScenario = {
                                        id: Date.now().toString(),
                                        name: '',
                                        condition: '',
                                        description: '',
                                        linked_fields: [],
                                        ...(activeCampaignId ? { campaignid: activeCampaignId } : {})
                                    };
                                    handleConvoConfigChange("scenarios", { scenarios: [...(editedSpace.convo_config.scenarios || []), newScenario] });
                                }}
                            />
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div
                                    className={styles.scenarioImportIconWrap}
                                    onClick={(e) => {
                                        setImportCalloutTarget(e.currentTarget);
                                        setIsImportCalloutVisible(true);
                                    }}
                                >
                                    <TbArrowAutofitUp size={20} title="Import Scenarios JSON" className={styles.scenarioExportIcon} style={{ marginLeft: '16px', verticalAlign: 'middle', cursor: 'pointer' }}/>
                                </div>
                                <TbArrowAutofitDown
                                    size={20}
                                    className={styles.scenarioExportIcon}
                                    style={{ marginLeft: '8px', verticalAlign: 'middle', cursor: 'pointer' }}
                                    title="Export Scenarios JSON"
                                    onClick={() => {
                                        const exportScenarios = filteredScenarios.map((s: any) => {
                                            const { campaignid, ...rest } = s;
                                            return rest;
                                        });
                                        const scenariosJson = JSON.stringify(exportScenarios, null, 2);
                                        navigator.clipboard
                                            .writeText(scenariosJson)
                                            .then(() => {
                                                toast.success("Scenarios JSON copied to clipboard!", { autoClose: 100 });
                                            })
                                            .catch(error => {
                                                console.error("Failed to copy scenarios:", error);
                                                toast.error("Failed to copy scenarios.");
                                            });
                                    }}
                                />
                            </div>
                        </div>

                        {isImportCalloutVisible && (
                            <Callout target={importCalloutTarget} onDismiss={() => setIsImportCalloutVisible(false)} directionalHint={DirectionalHint.bottomLeftEdge} gapSpace={10} isBeakVisible={false}>
                                <div style={{ padding: '20px', minWidth: '400px' }}>
                                    <TextField
                                        label="Paste Scenarios JSON"
                                        multiline
                                        rows={10}
                                        value={importScenariosJson}
                                        onChange={(_, newValue) => setImportScenariosJson(newValue || '')}
                                        styles={textFieldStylesScenarioMultilineV2}
                                    />
                                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                                        <PrimaryButton
                                            text="Import"
                                            onClick={() => {
                                                try {
                                                    const parsedScenarios = JSON.parse(importScenariosJson);
                                                    import.meta.env.DEV && console.log("‹imported_scenarios1›", parsedScenarios);
                                                    if (Array.isArray(parsedScenarios)) {
                                                        // tag imported scenarios with active campaign id if set
                                                        const tagged = activeCampaignId
                                                            ? parsedScenarios.map((s: any) => ({ ...s, campaignid: activeCampaignId }))
                                                            : parsedScenarios.map((s: any) => { const { campaignid, ...rest } = s; return rest; });
                                                        // merge: keep scenarios from other campaigns, replace current view
                                                        const allScenarios = editedSpace.convo_config.scenarios || [];
                                                        const otherScenarios = activeCampaignId
                                                            ? allScenarios.filter(s => s.campaignid !== activeCampaignId)
                                                            : allScenarios.filter(s => !!s.campaignid);
                                                        handleConvoConfigChange("scenarios", { scenarios: [...otherScenarios, ...tagged] });
                                                        setIsImportCalloutVisible(false);
                                                        setImportScenariosJson("");
                                                        toast.success("Scenarios imported successfully!", { autoClose: 100 });
                                                        import.meta.env.DEV && console.log("‹imported_scenarios›", tagged);
                                                    } else {
                                                        toast.error("Invalid format: Expected an array of scenarios");
                                                    }
                                                } catch (error) {
                                                    console.error("Failed to parse scenarios JSON:", error);
                                                    toast.error("Invalid JSON format");
                                                }
                                            }}
                                        />
                                        <DefaultButton
                                            text="Cancel"
                                            onClick={() => {
                                                setIsImportCalloutVisible(false);
                                                setImportScenariosJson("");
                                            }}
                                        />
                                    </div>
                                </div>
                            </Callout>
                        )}

                        {/* Scenarios */}
                        <div>
                            {filteredScenarios.map((scenario, index) => {
                                const fullIndex = editedSpace.convo_config.scenarios?.indexOf(scenario) ?? index;
                                const isCollapsed = collapsedScenarios[scenario.id];
                                return (
                                    <div
                                        key={scenario.id}
                                        className={styles.scenarioItem}
                                        draggable
                                        onDragStart={() => handleDragStart(fullIndex)}
                                        onDragOver={(e) => handleDragOver(e, fullIndex)}
                                        onDragEnd={handleDragEnd}
                                        style={{opacity: draggedScenarioIndex === fullIndex ? 0.5 : 1, cursor: 'move'}}
                                    >
                                        <div className={styles.scenarioItemCollapsable}>
                                            <IconButton
                                                iconProps={{ iconName: isCollapsed ? "ChevronRight" : "ChevronDown" }}
                                                onClick={() => setCollapsedScenarios(prev => ({ ...prev, [scenario.id]: !prev[scenario.id] }))}
                                                styles={{ root: { marginRight: 8, marginTop: 3, padding: 2, borderRadius: 3 } }}
                                                title={isCollapsed ? "Expand" : "Collapse"}
                                            />
                                            <div className={styles.scenarioItemHeader}>
                                                {isCollapsed ? (
                                                    <div className={styles.scenarioNameCollapsed}>{scenario.name}</div>
                                                ) : (
                                                    <TextField
                                                        label="Scenario Name"
                                                        value={scenario.name}
                                                        styles={textFieldTitleStylesSingleScenarios}
                                                        placeholder="short_descriptive_name"
                                                        disabled={isCollapsed}
                                                        onChange={(_, newValue) => {
                                                            const updatedScenarios = editedSpace.convo_config.scenarios.map((s, i) =>
                                                                i === fullIndex
                                                                    ? { ...s, name: newValue?.toLowerCase()?.replace(/[^А-Яа-яA-Za-z0-9]/g, "_") || '' }
                                                                    : s
                                                            );
                                                            handleConvoConfigChange("scenarios", { scenarios: updatedScenarios });
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            <IconButton
                                                styles={{ root: { marginTop: "0px", borderRadius: "3px", padding: "2px" } }}
                                                iconProps={{ iconName: "Delete" }}
                                                onClick={() => {
                                                    const updatedScenarios = editedSpace.convo_config.scenarios.filter((_, i) => i !== fullIndex);
                                                    handleConvoConfigChange("scenarios", { scenarios: updatedScenarios });
                                                }}
                                                className={styles.deleteButton}
                                                title="Delete Scenario"
                                            />
                                        </div>

                                        <div className={`${styles.collapsibleContent} ${isCollapsed ? styles.collapsed : ''}`}>
                                            <TextField
                                                label="Conditions"
                                                placeholder="Clear conditions stating when the scenario should be used, e.g. 'if user asks to register an appointment'"
                                                value={scenario.condition}
                                                styles={textFieldStylesSingleScenarios}
                                                onChange={(_, newValue) => {
                                                    const updatedScenarios = editedSpace.convo_config.scenarios.map((s, i) =>
                                                        i === fullIndex ? { ...s, condition: newValue || '' } : s
                                                    );
                                                    handleConvoConfigChange("scenarios", { scenarios: updatedScenarios });
                                                }}
                                            />
                                            <div className={styles.scenarioLabel}>Scenario Description</div>
                                            <div className={styles.scenarioDesc}>
                                                <HighlightedMultiline
                                                    value={scenario.description}
                                                    highlights={scenario.linked_fields}
                                                    onChange={(newValue) => {
                                                        const fieldNames = getRootFieldNames(docextractionconfig);
                                                        const linked = extractLinkedFields(newValue || '', fieldNames);
                                                        const updatedScenarios = editedSpace.convo_config.scenarios.map((s, i) =>
                                                            i === fullIndex ? { ...s, description: newValue || '', linked_fields: linked } : s
                                                        );
                                                        handleConvoConfigChange("scenarios", { scenarios: updatedScenarios });
                                                    }}
                                                />
                                            </div>

                                            <div style={{ margin: "8px 0" }}>
                                                {scenario.linked_fields.map(field => (
                                                    <div className={styles.linkedField} key={field}>
                                                        {field}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className={styles.editFormSectionTitle}>
                    <span>{"Domain Specific Words"}</span>
                </div>

                {/* Domain Specific Words */}
                <TagPicker
                    selectedItems={
                        activeImpWords
                            ? activeImpWords.split(";").filter(w => w.trim() !== "").map(w => ({ key: w.trim(), name: w.trim() }))
                            : []
                    }
                    onResolveSuggestions={() => []}
                    onEmptyResolveSuggestions={() => []}
                    onChange={(items?: ITag[]) => {
                        const tags = (items || []).map(i => i.name);
                        handleImpWordsChange(tags.join(";"));
                    }}
                    createGenericItem={(input: string) => ({ key: input, name: input } as ITag & { __isNew: true })}
                    styles={impWordsTagInputStyles}
                    onValidateInput={(input: string) => (input.trim() ? ValidationState.valid : ValidationState.invalid)}
                    inputProps={{
                        placeholder: "Add important word or phrase...",
                        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => {
                            const pastedText = e.clipboardData.getData("text");
                            if (pastedText && (pastedText.includes(",") || pastedText.includes(";") || pastedText.includes("\n"))) {
                                e.preventDefault();
                                const newItems = pastedText.split(/[,;\n\r]/).map(s => s.trim()).filter(s => s.length > 0);
                                const existing = activeImpWords
                                    ? activeImpWords.split(";").filter(w => w.trim() !== "")
                                    : [];
                                const merged = [...existing, ...newItems];
                                handleImpWordsChange(merged.join(";"));
                            }
                        }
                    }}

                    resolveDelay={200}
                />

                <div className={styles.docExtrSaveFixed}>
                    <div />
                    <PrimaryButton
                        text="Save"
                        onClick={() => handleSave()}
                        disabled={isLoadingAPI}
                        className={styles.saveSpaceButton}
                        styles={invFieldConfMainSaveButStyles}
                    />
                </div>
            </Stack>
        </div>
    );
};
